import React, { useState } from 'react';
import { api } from '../api';
import { compact } from '../lib/ui';
import { Donut, MiniBars } from '../lib/charts';
import { EmptyState } from '../lib/illustrations';
import {
  IconHeart,
  IconComment,
  IconShare,
  IconEye,
  IconCheckCircle,
  IconRefresh,
  IconChart,
} from '../lib/icons';

export default function Analytics({ summary, publications, platforms, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');

  const totals = (summary && summary.totals) || {};
  const engagement = totals.engagement || {};
  const byPlatform = (summary && summary.byPlatform) || [];
  const colorOf = (id) => {
    const p = platforms.find((x) => x.id === id);
    return p ? p.color : '#6d5cff';
  };
  const nameOf = (id) => {
    const p = platforms.find((x) => x.id === id);
    return p ? p.name : id;
  };

  const maxLikes = Math.max(1, ...byPlatform.map((b) => b.likes));

  const refreshAll = async () => {
    setBusy(true);
    setNote('');
    const done = publications.filter((p) => Object.keys(p.results || {}).some((k) => (p.results || {})[k].ok));
    const limit = 20;
    const targets = done.slice(0, limit);
    let failures = 0;
    try {
      for (const pub of targets) {
        try {
          await api.post(`/publications/${pub.id}/refresh-metrics`, {});
        } catch (err) {
          failures += 1;
        }
      }
      await onRefresh();
      const skipped = done.length - targets.length;
      const parts = [`Métricas atualizadas em ${targets.length - failures} publicação(ões).`];
      if (failures > 0) parts.push(`${failures} falharam.`);
      if (skipped > 0) parts.push(`${skipped} ficaram para depois (limite de ${limit}).`);
      setNote(parts.join(' '));
    } catch (err) {
      setNote(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="cards">
        <div className="card">
          <div className="card-top">
            <div className="k">Curtidas</div>
            <span className="card-icon">
              <IconHeart size={16} />
            </span>
          </div>
          <div className="v">{compact(engagement.likes)}</div>
        </div>
        <div className="card">
          <div className="card-top">
            <div className="k">Comentários</div>
            <span className="card-icon">
              <IconComment size={16} />
            </span>
          </div>
          <div className="v">{compact(engagement.comments)}</div>
        </div>
        <div className="card">
          <div className="card-top">
            <div className="k">Compartilhamentos</div>
            <span className="card-icon">
              <IconShare size={16} />
            </span>
          </div>
          <div className="v">{compact(engagement.shares)}</div>
        </div>
        <div className="card">
          <div className="card-top">
            <div className="k">Visualizações</div>
            <span className="card-icon">
              <IconEye size={16} />
            </span>
          </div>
          <div className="v">{compact(engagement.views)}</div>
        </div>
        <div className="card">
          <div className="card-top">
            <div className="k">Taxa de sucesso</div>
            <span className="card-icon">
              <IconCheckCircle size={16} />
            </span>
          </div>
          <div className="v">{totals.successRate || 0}%</div>
          <div className="s">
            {totals.successes || 0} envios com sucesso de {totals.attempts || 0}
          </div>
        </div>
      </div>

      {note ? <div className="banner info">{note}</div> : null}

      <section className="section">
        <div className="section-head">
          <h2>
            <span className="head-ico">
              <IconChart size={16} />
            </span>
            Desempenho por plataforma (curtidas)
          </h2>
          <button type="button" className="btn primary small" onClick={refreshAll} disabled={busy}>
            <span className="btn-ico">
              <IconRefresh size={14} />
            </span>
            {busy ? 'Atualizando…' : 'Atualizar métricas'}
          </button>
        </div>

        {byPlatform.length === 0 ? (
          <EmptyState kind="chart" size={170} title="Nenhum dado coletado ainda.">
            <span className="hint">Publique algo e depois clique em “Atualizar métricas”.</span>
          </EmptyState>
        ) : (
          <div className="analytics-top">
            <div className="bars">
              {byPlatform.map((b) => (
                <div className="bar-row" key={b.platform}>
                  <span>{nameOf(b.platform)}</span>
                  <div className="bar-track">
                    <div
                      className="bar-fill"
                      style={{
                        width: `${Math.round((b.likes / maxLikes) * 100)}%`,
                        background: `linear-gradient(90deg, ${colorOf(b.platform)}, #00d4a0)`,
                      }}
                    />
                  </div>
                  <span style={{ textAlign: 'right' }}>{compact(b.likes)}</span>
                </div>
              ))}
            </div>
            <div className="analytics-side">
              <div className="donut-wrap">
                <Donut
                  value={totals.successRate || 0}
                  size={150}
                  label="sucesso"
                  sub={`${totals.attempts || 0} envios`}
                />
              </div>
              <MiniBars
                data={byPlatform.map((b) => ({ label: nameOf(b.platform), value: b.likes }))}
                height={64}
                color="#6d5cff"
                color2="#00d4a0"
              />
            </div>
          </div>
        )}
      </section>

      <section className="section">
        <div className="section-head">
          <h2>
            <span className="head-ico">
              <IconChart size={16} />
            </span>
            Comparativo completo
          </h2>
        </div>
        {byPlatform.length === 0 ? (
          <div className="hint">Sem dados.</div>
        ) : (
          <div className="list">
            {byPlatform.map((b) => (
              <div className="row" key={b.platform}>
                <div>
                  <div className="title">{nameOf(b.platform)}</div>
                  <div className="meta">
                    {b.posts} publicações · {b.ok} ok · {b.failed} falhas
                  </div>
                </div>
                <div className="actions">
                  <span className="badge info">{compact(b.views)} views</span>
                  <span className="badge ok">{compact(b.likes)} likes</span>
                  <span className="badge warn">{compact(b.comments)} coment.</span>
                  <span className="badge muted">{compact(b.shares)} shares</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
